<script setup lang="ts">
import LazyImage from '~/components/LazyImage.vue';
import WishlistButton from '../WishlistButton.vue';
import { IconButton } from '@laam/ui/base';
import { useDeviceType } from '~/utils/use-device-type';

const { deviceType } = useDeviceType();
const ImageViewer = defineAsyncComponent(() => import('./ImageViewer.vue'));
const ImageViewerMobile = defineAsyncComponent(
	() => import('./ImageViewerMobile.vue'),
);

const GlobalStore = useGlobalStore();
const imgIndex = ref(0);

interface ImageSliderProps {
	media: {
		src: string;
		alt: string;
		height: number;
		width: number;
	}[];
	parent: string;
	alt: string;
	attributes: string[];
	node: string;
	shopifyId: number;
}

const props = defineProps<ImageSliderProps>();
const currentSlide = ref(0);
const slideTo = (val: number) => {
	currentSlide.value = val;
};

const fabricTag = computed(() => {
	const numberOfPieces = props.attributes['number-of-pieces'].charAt(0);
	let fabricType;
	if (props.node === 'shalwar' || props.node === 'trousers') {
		fabricType = props.attributes['bottom-fabric'];
	} else if (props.node === 'dupatta') {
		fabricType = props.attributes['dupatta-fabrics'];
	} else {
		fabricType = props.attributes['shirt-fabrics'];
	}
	if (numberOfPieces && fabricType) {
		return `${numberOfPieces}pc, ${capitalizeFirstWord(fabricType)}`;
	}
	return null;
});

const imageIndex = (index: number) => {
	imgIndex.value = index;
};

provide('imgIndex', imgIndex);
</script>
<template>
	<div class="gap-7xl flex flex-col relative">
		<Carousel
			v-model="currentSlide"
			:items-to-show="1"
			class="pdp--image-slider relative"
			:wrap-around="true"
			:transition="100"
		>
			<slide
				v-for="(slide, index) in props.media"
				:key="slide.src"
				class="min-h-[400px] relative"
				@click="imageIndex(index)"
			>
				<ImageViewerMobile
					v-if="deviceType !== 'desktop'"
					:media="media"
					:index="imgIndex"
				>
				</ImageViewerMobile>

				<ImageViewer v-else :media="media" :index="imgIndex"> </ImageViewer>
				<LazyImage
					class="pdp__image-slider-image image"
					:src="slide.src"
					:alt="props.alt"
					:width="deviceType !== 'desktop' ? 300 : 500"
					loading="lazy"
				/>
			</slide>

			<template #addons>
				<div
					v-if="fabricTag"
					class="absolute py-xxs pr-md pl-3xl bg-white bottom-[32px] left-0 rounded-r-small"
				>
					<span class="text-gray-700 font-medium text-sm">{{ fabricTag }}</span>
				</div>

				<div v-if="deviceType !== 'desktop'" class="flex">
					<IconButton
						v-if="GlobalStore.storeType !== 'OCTANE'"
						class="!rounded-full border-none !hover:bg-white bg-white p-xl absolute right-[14px] bottom-[32px] h-[44px] w-[44px] flex items-center justify-center"
						variant="secondary"
						size="lg"
					>
						<WishlistButton
							:product-id="props.shopifyId"
							:image-url="props.media[0]!.src"
							:image-alt="props.media[0]!.alt"
						/>
					</IconButton>
				</div>
				<navigation />
				<pagination />
			</template>
		</Carousel>

		<Carousel
			id="thumbnails"
			ref="carousel"
			v-model="currentSlide"
			class="lg:block hidden bg-white"
			:items-to-show="4"
			:snap-align="'start'"
		>
			<Slide v-for="(slide, index) in props.media" :key="slide.src">
				<div
					class="pr-md image-box w-full !min-w-max !min-h-none !bg-white cursor-pointer"
					@click="slideTo(index)"
				>
					<LazyImage
						class="rounded-xs w-full"
						:class="currentSlide === index ? '!opacity-100' : 'opacity-60'"
						:src="slide.src"
						:alt="props.alt"
						width="90"
						loading="lazy"
					/>
				</div>
			</Slide>
		</Carousel>
	</div>
</template>

<style>
/* .pdp--image-slider .carousel__item {
	@apply;
}

.pdp--image-slider .carousel__slide {
	@apply;
} */

.pdp--image-slider .carousel__prev,
.pdp--image-slider .carousel__next {
	@apply bg-white rounded-full p-[0.44rem] mx-3xl lg:p-lg lg:mx-5xl;
}

.pdp--image-slider .carousel__pagination {
	@apply absolute mx-auto left-[16px] px-xl py-md rounded-full right-[16px]  gap-sm w-fit bottom-sm;
}

.pdp--image-slider .carousel__pagination-item {
	@apply w-md;
}

.pdp--image-slider .carousel__pagination-button {
	@apply px-md;
}

.pdp--image-slider .carousel__pagination-button::after {
	@apply bg-white rounded-2xl size-sm opacity-55;
}

.pdp--image-slider .carousel__pagination-button--active::after {
	@apply bg-black opacity-100;
}

.carousel__icon {
	@apply h-7xl w-7xl;
}
</style>
